import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from '@mui/material';
import {
  People as PeopleIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  Shield as ShieldIcon,
  Refresh as RefreshIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminService,
  type SystemStats,
  type AuditLogResponse,
} from '../services/admin.service';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export const AdminDashboard = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recalculateSuccess, setRecalculateSuccess] = useState(false);
  const successTimeoutRef = useRef<ReturnType<
    typeof globalThis.setTimeout
  > | null>(null);

  // Redirect non-admin users
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        globalThis.clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Fetch admin stats
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery<SystemStats>({
    queryKey: ['adminStats'],
    queryFn: adminService.getStats,
  });

  // Fetch recent audit log
  const {
    data: auditLogResponse,
    isLoading: auditLoading,
    error: auditError,
  } = useQuery<AuditLogResponse>({
    queryKey: ['adminAuditLog', { limit: 10 }],
    queryFn: () => adminService.getAuditLog({ limit: 10 }),
  });

  const auditLog = auditLogResponse?.logs;

  // Recalculate scores mutation
  const recalculateMutation = useMutation({
    mutationFn: adminService.recalculateScores,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
      setRecalculateSuccess(true);
      successTimeoutRef.current = globalThis.setTimeout(
        () => setRecalculateSuccess(false),
        3000
      );
    },
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getActionChipColor = (
    action: string
  ):
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning' => {
    if (action.includes('DELETE') || action.includes('DEACTIVATE'))
      return 'error';
    if (action.includes('CREATE') || action.includes('ACTIVATE'))
      return 'success';
    if (action.includes('UPDATE') || action.includes('CHANGE'))
      return 'warning';
    return 'default';
  };

  if (statsLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (statsError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{t('admin.loadError')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {t('admin.dashboard.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/admin/users')}>
            {t('admin.manageUsers')}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/admin/teams')}>
            {t('admin.manageTeams')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/settings')}
          >
            {t('admin.systemSettings')}
          </Button>
        </Box>
      </Box>

      {recalculateSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('admin.recalculateSuccess')}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <PeopleIcon color="primary" />
                <Typography variant="h6">{stats?.users?.total ?? 0}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('admin.stats.totalUsers')}
              </Typography>
              <Typography variant="caption" color="success.main">
                {stats?.users?.active ?? 0} {t('admin.stats.active')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <GroupsIcon color="primary" />
                <Typography variant="h6">{stats?.teams?.total ?? 0}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('admin.stats.totalTeams')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <SecurityIcon color="primary" />
                <Typography variant="h6">
                  {stats?.components?.total ?? 0}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('admin.stats.totalComponents')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <WarningIcon color="warning" />
                <Typography variant="h6">{stats?.risks?.total ?? 0}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('admin.stats.totalRisks')}
              </Typography>
              <Typography variant="caption" color="error.main">
                {stats?.risks?.byStatus?.identified ?? 0}{' '}
                {t('admin.stats.openRisks')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <ShieldIcon color="success" />
                <Typography variant="h6">
                  {stats?.spaces?.total ?? 0}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('admin.stats.totalSpaces')}
              </Typography>
              <Typography variant="caption" color="info.main">
                {stats?.spaces?.public ?? 0} {t('admin.stats.public')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('admin.actions')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => recalculateMutation.mutate()}
            disabled={recalculateMutation.isPending}
          >
            {recalculateMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('admin.recalculateScores')
            )}
          </Button>
        </Box>
      </Paper>

      {/* Recent Audit Log */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">{t('admin.recentActivity')}</Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/admin/audit-log')}
          >
            {t('common.viewAll')}
          </Button>
        </Box>

        {auditLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : auditError ? (
          <Alert severity="error">{t('admin.auditLogError')}</Alert>
        ) : auditLog && auditLog.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.audit.timestamp')}</TableCell>
                  <TableCell>{t('admin.audit.action')}</TableCell>
                  <TableCell>{t('admin.audit.user')}</TableCell>
                  <TableCell>{t('admin.audit.details')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLog.map(entry => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(entry.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.action}
                        size="small"
                        color={getActionChipColor(entry.action)}
                      />
                    </TableCell>
                    <TableCell>{entry.user?.name || entry.userId}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {entry.description || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary">
            {t('admin.noActivity')}
          </Typography>
        )}
      </Paper>
    </Container>
  );
};
